import React, { useState } from "react";
import { Input, Label, FormFeedback, Popover, PopoverBody } from "reactstrap";
import Select from "react-select";

interface OptionItem {
  label: string;
  value: string | number;
}

interface CustomInputProps {
  name: string;
  label: string;
  type: any;
  placeholder?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  touched?: boolean;
  required?: boolean;
  isPassword?: boolean;
  dropdownOptions?: OptionItem[];
  isTooltip?: boolean;
  tooltipMessage?: string;
  isDisabled?: boolean;
  onSearch?: (searchTerm: string) => void; // New prop for API search
  isLoadingOptions?: boolean; // New prop to show loading state
  isPound?: boolean;
}

const CustomInput: React.FC<CustomInputProps> = ({
  name,
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  touched,
  required,
  isPassword,
  dropdownOptions,
  isTooltip,
  tooltipMessage,
  isDisabled = false,
  onSearch,
  isLoadingOptions = false,
  isPound = false,
}) => {
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const inputType = isPassword ? (passwordShow ? "text" : "password") : type;
  const isSelect = type === "select";
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const onToggleVisibility = () => {
    setPasswordShow((prev) => !prev);
  };

  const hasError = touched && !!error;

  const toggleTooltip = () => {
    setShowTooltip((prev) => !prev);
  };

  // Handle react-select change.
  // After firing onChange (which updates Formik's value), we defer onBlur via
  // setTimeout(0) so React flushes the value state update first. When onBlur
  // then fires, Formik validates against the already-updated value and the
  // field is marked touched — so errors clear immediately on selection.
  const handleSelectChange = (selectedOption: OptionItem | null) => {
    const syntheticEvent = {
      target: {
        name: name,
        value: selectedOption?.value ?? "",
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);

    setTimeout(() => {
      if (onBlur) {
        const syntheticBlurEvent = {
          target: { name },
        } as React.FocusEvent<HTMLInputElement>;
        onBlur(syntheticBlurEvent);
      }
    }, 0);
  };

  // Handle react-select blur
  const handleSelectBlur = () => {
    if (onBlur) {
      const syntheticEvent = {
        target: {
          name: name,
        },
      } as React.FocusEvent<HTMLInputElement>;

      onBlur(syntheticEvent);
    }
  };

  // Handle input change for search
  const handleInputChange = (inputValue: string, actionMeta: any) => {
    // Only trigger search on user input, not on other actions
    if (actionMeta.action === "input-change" && onSearch) {
      onSearch(inputValue);
      setSearchTerm(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart ?? 0;
      const valueLength = input.value.length;
      // 🚫 Block space at start
      if (cursorPosition === 0) {
        e.preventDefault();
        return;
      }

      // 🚫 Block space at end
      if (isPassword && cursorPosition === valueLength) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleBlurWithTrim = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = e.target.value.trim();

    const modifiedEvent = {
      ...e,
      target: {
        ...e.target,
        name,
        value: trimmedValue,
      },
    };

    onChange(modifiedEvent as React.ChangeEvent<HTMLInputElement>);
    onBlur?.(modifiedEvent);
  };

  return (
    <div className="mb-3">
      <div className="d-flex gap-1 align-content-center">
        <Label
          htmlFor={name}
          className="font-primary fw-normal fs-14 text-black2D d-flex"
        >
          {label} {required && <span className="text-danger">*</span>}
          {isTooltip ? (
            <>
              <i
                className="ri-error-warning-line text-black2D ms-1 btn-press-effect"
                style={{ fontSize: 18 }}
                id={`${name}-tooltip`}
              />
              <Popover
                placement="auto"
                isOpen={showTooltip}
                target={`${name}-tooltip`}
                toggle={toggleTooltip}
                trigger="legacy"
                popperClassName="br-10 p-2"
                innerClassName="p-0 m-0"
              >
                <PopoverBody className="bg-white font-primary fs-16 fw-normal text-black2D ">
                  {tooltipMessage}
                </PopoverBody>
              </Popover>
            </>
          ) : null}
        </Label>
      </div>

      <div
        className={`position-relative ${
          isPassword ? "auth-pass-inputgroup" : ""
        }`}
      >
        {isSelect && dropdownOptions ? (
          <>
            <Select
              inputId={name}
              name={name}
              options={dropdownOptions}
              value={dropdownOptions.find((opt) => opt.value === value) || null}
              onChange={handleSelectChange}
              onBlur={handleSelectBlur}
              onInputChange={handleInputChange}
              classNamePrefix="react-select"
              className={`auth-form-dropdown-control ${
                hasError ? "is-invalid" : ""
              } `}
              placeholder={placeholder || "Select an option"}
              isDisabled={isDisabled}
              isSearchable={true}
              isClearable={false}
              isLoading={isLoadingOptions}
              loadingMessage={() =>
                searchTerm?.length > 0 ? "Searching..." : "Loading..."
              }
              noOptionsMessage={() => "No options found"}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                menuList: (base: any) => ({
                  ...base,
                  maxHeight: "200px",
                  overflowY: "auto",
                }),
                control: (base: any) => ({
                  ...base,
                  borderColor: hasError ? "#dc3545" : base.borderColor,
                  backgroundColor: isDisabled
                    ? "#F5F6FB"
                    : base.backgroundColor,
                  "&:hover": {
                    borderColor: hasError ? "#dc3545" : base.borderColor,
                  },
                }),
              }}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{error}</div>
            )}
          </>
        ) : (
          <>
            {isPound && (
              <div
                className="position-absolute start-0 top-0 p-2 ms-1 fs-15"
                style={{ marginTop: "2px" }}
              >
                £
              </div>
            )}
            <Input
              id={name}
              name={name}
              type={inputType}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onWheel={(e: any) => e.currentTarget.blur()}
              onBlur={handleBlurWithTrim}
              invalid={hasError}
              className={`auth-form-control ${isPound ? "ps-4" : ""}`}
              disabled={isDisabled}
              onKeyDown={handleKeyDown}
            />
            {hasError && <FormFeedback type="invalid">{error}</FormFeedback>}
          </>
        )}

        {isPassword && !hasError && (
          <button
            type="button"
            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
            onClick={onToggleVisibility}
          >
            <i
              className={
                !passwordShow
                  ? "ri-eye-off-fill align-middle"
                  : "ri-eye-fill align-middle"
              }
            ></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomInput;
